import * as React from "react"
import {
  DimensionSortMode,
  DirectionFromOrigin,
  type IntervalOrigin,
  type SummaryFilterInput,
  useSummarizeTransactionsByCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import {
  type ActiveCategory,
  convertToActiveCategoriesTree,
} from "src/shared/helpers/hierarchicalCategoryHelper"
import { convertActiveHierarchicalDimensionTreeToFlatList } from "src/shared/helpers/hierarchicalDimensionHelper"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

export function useCategoryFilters({
  filters,
  searchTerm,
  origin,
  skip = false,
}: {
  filters: Omit<SummaryFilterInput, "viewKey">
  searchTerm: string
  origin?: IntervalOrigin
  skip?: boolean
}) {
  const viewKey = useViewVersion()
  const intervalOrigin = useIntervalOrigin()

  const { data, loading } = useSummarizeTransactionsByCategoryQuery({
    variables: {
      limit: 100,
      sort: DimensionSortMode.Active,
      filter: {
        ...filters,
        viewKey,
      },
      direction: DirectionFromOrigin.Past,
      origin: {
        ...(origin || intervalOrigin),
        intervalCount: origin?.intervalCount || intervalOrigin.intervalCount || 1,
      },
    },
    skip,
  })

  const activeCategories = React.useMemo(
    () =>
      data?.dimensionalTransactionSummary.category
        .filter((summary) =>
          summary.categoryObject.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map<ActiveCategory>((c) => ({
          category: c.categoryObject,
          count: c.summary.total.transactionsCount,
        })) || [],
    [data?.dimensionalTransactionSummary.category, searchTerm]
  )

  const categoryNodes = React.useMemo(
    () =>
      activeCategories
        ? convertActiveHierarchicalDimensionTreeToFlatList(
            convertToActiveCategoriesTree(activeCategories, 0)
          )
        : [],
    [activeCategories]
  )

  return { activeCategories, categoryNodes, loading }
}
